import { forwardRef, useState } from 'react';

const Contact = forwardRef<HTMLElement>((props, ref) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    institution: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('institution', formData.institution);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6pi16cddmmni7ck8350', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', institution: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section
      ref={ref}
      className="bg-[#F8F6F3] py-16 md:py-[200px] opacity-0 transition-all duration-[800ms]"
      id="contact"
    >
      <div className="max-w-[1000px] mx-auto px-6 md:px-8">

        {/* Header */}
        <div className="text-center mb-12 md:mb-[100px]">
          <p className="text-[#3A3D42] text-[11px] uppercase tracking-[0.10em] font-sans mb-5 md:mb-[40px]">
            CONFIDENTIAL INQUIRY
          </p>
          <h2
            className="font-serif leading-[1.1] tracking-[-0.01em] text-black mx-auto mb-5 md:mb-[32px]"
            style={{
              fontSize: 'clamp(32px, 6vw, 64px)',
              fontFamily: '"Playfair Display", Georgia, serif',
              maxWidth: '700px',
            }}
          >
            Initiate Strategic Dialogue
          </h2>
          <p className="text-[#6B6B6B] font-sans mx-auto leading-[1.7]" style={{ fontSize: 'clamp(14px, 2vw, 16px)', maxWidth: '560px' }}>
            For sensitive matters requiring discretion and institutional expertise, please provide your details below. All inquiries are handled with the utmost confidentiality.
          </p>
        </div>

        {/* Presence markers */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-[#3A3D42]/20 mb-10 md:mb-[80px] max-w-[900px] mx-auto">
          {[
            { city: 'Nairobi', region: 'East Africa' },
            { city: 'New York', region: 'North America' },
            { city: 'Washington DC', region: 'North America' },
          ].map((office, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center px-6 py-8 md:px-[40px] md:py-[48px] border-b border-[#3A3D42]/20 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
            >
              <div className="w-[24px] h-[24px] flex items-center justify-center mb-4">
                <div className="relative flex items-center justify-center" style={{ width: '20px', height: '20px' }}>
                  <div className="absolute inset-0 border border-[#C9A96E]/40" style={{ borderRadius: '1px', transform: 'rotate(45deg)' }} />
                  <div className="absolute w-[5px] h-[5px] bg-[#C9A96E]/60" style={{ transform: 'rotate(45deg)' }} />
                </div>
              </div>
              <p
                className="font-serif text-black mb-2"
                style={{ fontSize: '18px', fontFamily: '"Playfair Display", Georgia, serif', letterSpacing: '0.06em' }}
              >
                {office.city}
              </p>
              <p className="text-[#6B6B6B]/60 font-sans uppercase" style={{ fontSize: '9px', letterSpacing: '0.22em' }}>
                {office.region}
              </p>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-[700px] mx-auto" data-readdy-form id="contact-form">
          <div className="mb-5 md:mb-[32px]">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Full Name"
              required
              className="w-full h-[52px] md:h-[64px] bg-transparent border border-[#3A3D42]/30 px-4 md:px-[24px] text-[15px] md:text-[16px] font-sans text-black placeholder:text-[#6B6B6B]/50 focus:outline-none focus:border-[#3A3D42] transition-colors duration-300"
            />
          </div>

          <div className="mb-5 md:mb-[32px]">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Institutional Email"
              required
              className="w-full h-[52px] md:h-[64px] bg-transparent border border-[#3A3D42]/30 px-4 md:px-[24px] text-[15px] md:text-[16px] font-sans text-black placeholder:text-[#6B6B6B]/50 focus:outline-none focus:border-[#3A3D42] transition-colors duration-300"
            />
          </div>

          <div className="mb-5 md:mb-[32px]">
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              placeholder="Institution / Organization"
              required
              className="w-full h-[52px] md:h-[64px] bg-transparent border border-[#3A3D42]/30 px-4 md:px-[24px] text-[15px] md:text-[16px] font-sans text-black placeholder:text-[#6B6B6B]/50 focus:outline-none focus:border-[#3A3D42] transition-colors duration-300"
            />
          </div>

          <div className="mb-8 md:mb-[48px]">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Brief Description of Strategic Need"
              required
              maxLength={500}
              rows={5}
              className="w-full bg-transparent border border-[#3A3D42]/30 px-4 py-4 md:px-[24px] md:py-[20px] text-[15px] md:text-[16px] font-sans text-black placeholder:text-[#6B6B6B]/50 focus:outline-none focus:border-[#3A3D42] transition-colors duration-300 resize-none"
            />
            <p className="text-[12px] text-[#6B6B6B]/60 mt-2 font-sans">
              {formData.message.length}/500 characters
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-[52px] md:h-[64px] bg-black text-white text-[11px] uppercase tracking-[0.08em] font-sans hover:bg-[#3A3D42] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
          </button>

          {submitStatus === 'success' && (
            <p className="mt-6 text-center text-[14px] font-sans text-black">
              Thank you. Your inquiry has been received. We will respond within 48 hours.
            </p>
          )}
          {submitStatus === 'error' && (
            <p className="mt-6 text-center text-[14px] font-sans text-red-600">
              An error occurred. Please try again or contact us directly.
            </p>
          )}
        </form>
      </div>
    </section>
  );
});

Contact.displayName = 'Contact';

export default Contact;
